/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, Card, Dropdown, Switch, Tooltip, Spin } from "antd";
import { SettingOutlined, InfoCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import "./index.scss";

const TopCard = ({ cards: initialCards, loading = false, onCardClick }) => {
  const [cards, setCards] = useState(initialCards || []);
  const [visibleCards, setVisibleCards] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Sync cards prop with internal state when prop changes
  useEffect(() => {
    if (initialCards && Array.isArray(initialCards)) {
      setCards(initialCards);
    }
  }, [initialCards]);

  // Handle toggle for card visibility
  const handleToggle = (cardId, checked) => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === cardId ? { ...card, visible: checked } : card
      )
    );
  };

  // Update visible cards when card visibility changes - show all enabled cards dynamically
  useEffect(() => {
    const enabledCards = cards.filter((card) => card.visible);
    setVisibleCards(enabledCards);
  }, [cards]);

  // Handle clicks outside dropdown to close it, but keep it open when clicking inside
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (event) => {
      const dropdownElement = document.querySelector(".card-settings-dropdown");
      const triggerElement = event.target.closest(".settings-icon") ||
        event.target.closest("[class*=\"ant-dropdown-trigger\"]");

      // If click is inside dropdown menu, don't close
      if (dropdownElement?.contains(event.target)) {
        return;
      }

      // If clicking the trigger icon, toggle (handled separately)
      if (triggerElement) {
        return;
      }

      // Close dropdown if clicking outside
      setDropdownOpen(false);
    };

    // Add event listener with a small delay to avoid immediate closing
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [dropdownOpen]);

  // Create dropdown menu with card names and toggles - dynamically from API data
  const dropdownMenu = useMemo(() => {
    return {
      items: cards.map((card) => ({
        key: card.id,
        label: (
          <div
            className="dropdown-item"
            onClick={(e) => {
              if (e && e.stopPropagation) {
                e.stopPropagation();
              }
            }}
            onMouseDown={(e) => {
              if (e && e.stopPropagation) {
                e.stopPropagation();
              }
            }}
          >
            <span className="card-name">{card.name}</span>
            <div
              onClick={(e) => {
                if (e && e.stopPropagation) {
                  e.stopPropagation();
                }
              }}
              onMouseDown={(e) => {
                if (e && e.stopPropagation) {
                  e.stopPropagation();
                }
              }}
            >
              <Switch
                checked={card.visible}
                onChange={(checked, event) => {
                  if (event && event.stopPropagation) {
                    event.stopPropagation();
                  }
                  handleToggle(card.id, checked);
                }}
                size="small"
              />
            </div>
          </div>
        ),
      })),
      onClick: () => {
        // Don't close dropdown when clicking menu items - return nothing to prevent default close
        return;
      },
    };
  }, [cards]);

  return (
    <div className="top-card-container">
      <Card className="top-card-wrapper">
        <div className="top-card-header">
          <Dropdown
            ref={dropdownRef}
            menu={dropdownMenu}
            trigger={["click"]}
            overlayClassName="card-settings-dropdown"
            placement="bottomRight"
            open={dropdownOpen}
            onOpenChange={(open, info) => {
              // Prevent closing when clicking inside the menu
              // Only close when clicking outside (handled by useEffect) or on trigger
              if (!open && info?.source === "trigger") {
                // User clicked the trigger to close
                setDropdownOpen(false);
              } else if (open) {
                // Opening the dropdown
                setDropdownOpen(true);
              }
              // Don't close when clicking inside menu items
            }}
            destroyPopupOnHide={false}
          >
            <SettingOutlined
              className="settings-icon"
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen(!dropdownOpen);
              }}
            />
          </Dropdown>
        </div>

        <Spin spinning={loading}>
          <Row gutter={[16, 16]} className="top-card-content">
            {visibleCards.length > 0 ? (
              visibleCards.map((card) => {
                // Dynamic column span based on number of visible cards
                const count = visibleCards.length;
                let colSpan;
                if (count === 1) {
                  colSpan = { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 };
                } else if (count === 2) {
                  colSpan = { xs: 24, sm: 12, md: 12, lg: 12, xl: 12 };
                } else if (count === 3) {
                  colSpan = { xs: 24, sm: 12, md: 8, lg: 8, xl: 8 };
                } else if (count === 4) {
                  colSpan = { xs: 24, sm: 12, md: 6, lg: 6, xl: 6 };
                } else {
                  // For 5 or more cards, use responsive grid (up to 6 per row on xl screens)
                  colSpan = { xs: 24, sm: 12, md: 8, lg: 6, xl: 4 };
                }

                return (
                  <Col {...colSpan} key={card.id}>
                    <Card
                      className="metric-card"
                      onClick={() => onCardClick && onCardClick(card)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="metric-card-content">
                        <div className="metric-label">
                          <span>{card.name}</span>
                          {card.statuses && card.statuses.length > 0 && (
                            <Tooltip title={card.statuses.join(", ")}>
                              <InfoCircleOutlined style={{ marginLeft: 8, color: "#1890ff" }} />
                            </Tooltip>
                          )}
                        </div>
                        <div className="metric-value">
                          {card.value !== null ? card.value.toLocaleString() : "—"}
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })
            ) : (
              <Col span={24}>
                <div className="no-cards-message">
                  No cards enabled. Please enable cards from settings.
                </div>
              </Col>
            )}
          </Row>
        </Spin>
      </Card>
    </div>
  );
};

TopCard.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      visible: PropTypes.bool,
    })
  ).isRequired,
  onCardClick: PropTypes.func,
  loading: PropTypes.bool,
};

export default TopCard;

